/* eslint-disable react-refresh/only-export-components */
import { graphql } from '../../gql/index.ts';
import { GetUserInfoQuery } from '../../gql/graphql';
import { createContext, useEffect, useState } from 'react';
import { UseQueryExecute, UseQueryState, useQuery } from 'urql';
import { usePublicCompany } from '../hooks/usePublicCompany';

export type UserInfo = GetUserInfoQuery['currentUser'];

interface PublicUserContextProps {
  children: React.ReactNode;
}

export type PublicUserContextType = {
  userInfo:
    | UseQueryState<
        GetUserInfoQuery,
        {
          companyId: string;
        }
      >
    | undefined;
  refetchUserInfo: UseQueryExecute;
};

export const PublicUserInfoContext = createContext<PublicUserContextType>({
  userInfo: undefined,
  refetchUserInfo: () => {},
});

const CurrentUserQuery = graphql(/* GraphQL */ `
  query getUserInfo($companyId: String!) {
    currentUser(companyId: $companyId) {
      id
      allowSevenDaysReminder
      allowOneDayReminder
      privileges
      address {
        title
        firstName
        lastName
        email
      }
      lecturer {
        id
        jobTitle
        vita
        allowSevenDaysReminderForLecturer
        allowOneDayReminderForLecturer
        pathToProfileImage
      }
    }
  }
`);

// This Provider fetches and provides information about the current user, which is needed in multiple places
export const PublicUserContextProvider: React.FC<PublicUserContextProps> = ({
  children,
}) => {
  const [userInfo, setUserInfo] = useState<
    | UseQueryState<
        GetUserInfoQuery,
        {
          companyId: string;
        }
      >
    | undefined
  >(undefined);

  const [publicCompany] = usePublicCompany();

  const [currentUserQueryResult, reExecuteQuery] = useQuery({
    query: CurrentUserQuery,
    variables: { companyId: publicCompany?.id || '' },
  });

  useEffect(() => {
    if (currentUserQueryResult) {
      // Mirror urql query state into context-exposed userInfo.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setUserInfo(currentUserQueryResult);
    }
  }, [currentUserQueryResult]);

  return (
    <PublicUserInfoContext.Provider
      value={{ userInfo, refetchUserInfo: reExecuteQuery }}
    >
      {children}
    </PublicUserInfoContext.Provider>
  );
};
